import {useCallback, useEffect, useState, useTransition} from 'react'
import {ActionMenu, Box, Button, InlineLink, Stack, Text} from '@primer/react-brand'
import {CheckIcon, CopyIcon} from '@primer/octicons-react'

import styles from './CliInstallCta.module.css'
import {getAnalyticsEvent} from '../../../../../lib/utils/analytics'
import {t} from '../../../../../lib/utils/localization'

const installMethodConfig = {
  script: {label: 'Install with script', command: 'curl -fsSL https://gh.io/copilot-install | bash'},
  winget: {label: 'Install with WinGet', command: 'winget install GitHub.Copilot'},
  homebrew: {label: 'Install with Homebrew', command: 'brew install copilot-cli'},
  npm: {label: 'Install with npm', command: 'npm install -g @github/copilot'},
} satisfies Record<string, {label: string; command: string}>

type InstallMethod = keyof typeof installMethodConfig

const installMethods = Object.keys(installMethodConfig) as InstallMethod[]

function detectDefaultMethod(): InstallMethod {
  if (typeof navigator === 'undefined') return 'script'
  const ua = navigator.userAgent?.toLowerCase() ?? ''
  if (ua.includes('win')) return 'winget'
  if (ua.includes('mac')) return 'homebrew'
  return 'script'
}

/**
 * CLI install CTA rendered on the /features/copilot/cli page.
 * ActionMenu lets users pick their install method, then shows the matching command with a copy button.
 *
 * Rendered by FlexTemplateContent when the page path matches and the
 * `copilot_cli_install_cta` feature flag is enabled.
 */
export function CliInstallCta() {
  const [selectedMethod, setSelectedMethod] = useState<InstallMethod>(detectDefaultMethod)
  const [hasCopied, setHasCopied] = useState(false)
  const [, startTransition] = useTransition()

  const {label: selectedLabel, command} = installMethodConfig[selectedMethod]

  const handleCopy = useCallback(async () => {
    if (
      typeof navigator === 'undefined' ||
      !navigator.clipboard ||
      typeof navigator.clipboard.writeText !== 'function'
    ) {
      return
    }

    try {
      await navigator.clipboard.writeText(command)
      setHasCopied(true)
    } catch {
      // Silently ignore clipboard errors to avoid showing a false success state
    }
  }, [command])

  useEffect(() => {
    if (hasCopied) {
      const timer = setTimeout(() => {
        startTransition(() => setHasCopied(false))
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [hasCopied, startTransition])

  return (
    <Stack direction="vertical" padding="none" className={styles.cliInstallCta}>
      {/* Desktop: full install method picker */}
      <div className={styles.desktopOnly}>
        <Box className={styles.customInputContainer} marginBlockEnd={20}>
          <Stack padding="none" direction={{narrow: 'vertical', regular: 'horizontal'}} gap="none" alignItems="center">
            <ActionMenu onSelect={newValue => setSelectedMethod(newValue as InstallMethod)}>
              <ActionMenu.Button className={styles.installMethodButton}>{t(selectedLabel)}</ActionMenu.Button>
              <ActionMenu.Overlay aria-label={t('Select install method')}>
                {installMethods.map(method => (
                  <ActionMenu.Item
                    key={method}
                    value={method}
                    selected={method === selectedMethod}
                    {...getAnalyticsEvent({
                      action: 'select_install_option',
                      tag: 'button',
                      location: 'hero',
                      context: 'cli_install_cta',
                    })}
                  >
                    {t(installMethodConfig[method].label)}
                  </ActionMenu.Item>
                ))}
              </ActionMenu.Overlay>
            </ActionMenu>
            <Text as="span" font="monospace" size="100" className={styles.customInputCode}>
              {command}
            </Text>
            <Button
              trailingVisual={
                hasCopied ? (
                  <CheckIcon className={styles.customInputCopyIcon} />
                ) : (
                  <CopyIcon className={styles.customInputCopyIcon} />
                )
              }
              variant="accent"
              className={styles.customInputCopyButton}
              onClick={handleCopy}
              aria-label={t('Copy installation command')}
              {...getAnalyticsEvent({
                action: 'copy_command',
                tag: 'button',
                location: 'hero',
                context: 'cli_install_cta',
              })}
            >
              {t('Copy')}
            </Button>
            <span className="visually-hidden" role="status">
              {hasCopied ? t('Copied to clipboard') : ''}
            </span>
          </Stack>
        </Box>
        <Text as="p" size="200" variant="muted">
          <InlineLink
            href="https://docs.github.com/en/copilot/how-tos/copilot-cli/cli-best-practices"
            {...getAnalyticsEvent({
              action: 'view_docs',
              tag: 'button',
              location: 'hero',
              context: 'cli_install_cta',
            })}
          >
            {t('Read the documentation.')}
          </InlineLink>{' '}
          {t('Available with Copilot Free, Pro, Pro+, Business, and Enterprise.')}
        </Text>
      </div>

      {/* Mobile: simplified CTA */}
      <div className={styles.mobileOnly}>
        <Stack direction="vertical" padding="none" gap="normal" alignItems="center">
          <Button
            as="a"
            href="https://github.com/github/copilot-cli"
            variant="accent"
            {...getAnalyticsEvent({
              action: 'get_copilot_cli',
              tag: 'button',
              location: 'hero',
              context: 'cli_install_cta',
            })}
          >
            {t('Get Copilot CLI')}
          </Button>
          <Text as="p" size="200" variant="muted">
            <InlineLink
              href="https://docs.github.com/en/copilot/how-tos/copilot-cli/cli-best-practices"
              {...getAnalyticsEvent({
                action: 'view_docs',
                tag: 'button',
                location: 'hero',
                context: 'cli_install_cta',
              })}
            >
              {t('Read the documentation.')}
            </InlineLink>{' '}
            {t('Available with Copilot Free, Pro, Pro+, Business, and Enterprise.')}
          </Text>
        </Stack>
      </div>
    </Stack>
  )
}
